/* eslint-disable no-undef */

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import fetchData from "../../functions/fetchData";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { getHeaders } from "../../utility/getHeader";
import CardComponent from "../../components/cards/CardComponent";
import Header from "../../components/Header";
import Layout from "../../layout/Layout";
import { FaPrint, FaReceipt, FaMoneyBillWave, FaUser, FaCalendarAlt, FaCreditCard } from "react-icons/fa";
import "./vouchers.css";

export default function Vouchers() {
    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState("");
    const [outstandingAmount, setOutstandingAmount] = useState(0);
    const [paymentAmount, setPaymentAmount] = useState("");
    const [paymentMode, setPaymentMode] = useState("");
    const [referenceNumber, setReferenceNumber] = useState("");
    const [remarks, setRemarks] = useState("");
    const [balanceAfterPayment, setBalanceAfterPayment] = useState(0);
    const [data, setData] = useState([]);
    const [companyInfo, setCompanyInfo] = useState({});
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterMode, setFilterMode] = useState("All");
    const [bankAccounts, setBankAccounts] = useState([]);
    const [bankAccountId, setBankAccountId] = useState("");
    const [tdsAmount, setTdsAmount] = useState("");
    const [isTdsReceivable, setIsTdsReceivable] = useState(false);
    
    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [recordsPerPage, setRecordsPerPage] = useState(10);
    
    // Payment modes with bank account requirement
    const paymentModes = [
        { value: "Cash", label: "💵 Cash / เงินสด", requiresBank: false },
        { value: "Bank", label: "🏦 Bank Transfer / โอนเงิน", requiresBank: true },
        { value: "Credit_Card", label: "💳 Credit Card / บัตรเครดิต", requiresBank: true },
        { value: "UPI", label: "📱 UPI / ยูพีไอ", requiresBank: true },
        { value: "Cheque", label: "📝 Cheque / เช็ค", requiresBank: true },
        { value: "Online", label: "💻 Online Payment / จ่ายออนไลน์", requiresBank: true }
    ];
    
    // Fetch customers, bank accounts and company info when component loads
    useEffect(() => {
        fetchData("customers", setCustomers, "id", {});
        fetchBankAccounts();
        fetchCompanyInfo();
    }, []);

    const fetchBankAccounts = async () => {
        try {
            await fetchData("bank_accounts", setBankAccounts, "id", {});
        } catch (error) {
            console.error('Error fetching bank accounts:', error);
        }
    };

    const fetchCompanyInfo = async () => {
        try {
            const companyData = await fetchData("company_profile", null, "id", {});
            if (companyData && companyData.length > 0) {
                setCompanyInfo(companyData[0]);
            }
        } catch (error) {
            console.error("Error fetching company info:", error);
        }
    };
    const getOutStandingBalance = async (e) => {
        const customerId = e.target.value;
        setSelectedCustomer(customerId); // Update selected customer state

        if (!customerId) {
            setOutstandingAmount(0);
            return;
        }

        try {
            const res = await axios.get(`/customer-ledger-balance/${customerId}`, getHeaders());
            if (res.data.success) {
                // Use the balance from the API response
                const balance = res.data.data.balance || 0;
                setOutstandingAmount(Math.abs(balance)); // Show absolute value as outstanding amount
            } else {
                toast.error("Failed to fetch customer outstanding balance");
            }
        } catch (error) {
            console.error("Error fetching outstanding balance:", error);
            toast.error("Error fetching outstanding balance.");
        }
    };
    useEffect(() => {
        // Calculate balance after payment
        // If TDS is deducted, total payment = amount received + TDS amount
        const payment = parseFloat(paymentAmount) || 0;
        const tds = isTdsReceivable ? (parseFloat(tdsAmount) || 0) : 0;
        const totalPayment = payment + tds; // Total payment includes both received amount and TDS
        const remaining = outstandingAmount - totalPayment;
        setBalanceAfterPayment(remaining >= 0 ? remaining : 0);
    }, [paymentAmount, outstandingAmount, tdsAmount, isTdsReceivable]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!selectedCustomer || !paymentAmount || !paymentMode) {
            toast.error("Please fill in all required fields");
            return;
        }

        // Get customer name from the customers array
        const selectedCustomerData = customers.find(c => c.id === parseInt(selectedCustomer));
        const customerName = selectedCustomerData ? selectedCustomerData.name : 'Unknown Customer';

        const paymentData = {
            customer_id: selectedCustomer,
            customer_name: customerName,
            amount: parseFloat(paymentAmount),
            payment_mode: paymentMode,
            bank_account_id: bankAccountId || null,
            reference_number: referenceNumber,
            remarks: remarks,
            description: remarks,
            receipt_date: new Date().toISOString().split('T')[0],
            tds_amount: isTdsReceivable ? parseFloat(tdsAmount) || 0 : 0,
            is_tds_receivable: isTdsReceivable
        };

        setLoading(true);
        try {
            const res = await axios.post("/receipt-vouchers", paymentData, getHeaders());
            
            if (res.data.success) {
                toast.success("Receipt voucher created successfully!");
                await fetchData("receipt_vouchers", setData, "id", {});

                // Reset form
                setSelectedCustomer("");
                setPaymentAmount("");
                setPaymentMode("");
                setBankAccountId("");
                setReferenceNumber("");
                setRemarks("");
                setTdsAmount("");
                setIsTdsReceivable(false);
                setOutstandingAmount(0);
                setBalanceAfterPayment(0);
            } else {
                toast.error(res.data.message || "Failed to create receipt voucher");
            }
        } catch (error) {
            console.error("Error saving receipt:", error);
            toast.error(`Error recording receipt: ${error?.response?.data?.message || error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handlePrintReceipt = async (voucherData) => {
        try {
            setLoading(true);
            
            // Get customer details
            const customer = customers.find(c => c.id === parseInt(voucherData.customer_id));
            const customerName = customer ? customer.name : (voucherData.customer_name || 'N/A');
            const customerAddress = customer ? customer.address || 'N/A' : 'N/A';
            const customerPhone = customer ? customer.phone || 'N/A' : 'N/A';
            
            // Use correct date field and handle invalid dates
            const receiptDate = voucherData.receipt_date || voucherData.payment_date || voucherData.created_at;
            const paymentDate = receiptDate ? new Date(receiptDate) : new Date();
            
            // Check if date is valid
            const isValidDate = !isNaN(paymentDate.getTime());
            
            const formattedDate = isValidDate ? paymentDate.toLocaleDateString('th-TH', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            }) : 'วันที่ไม่ระบุ';
            
            const formattedTime = isValidDate ? paymentDate.toLocaleTimeString('th-TH', {
                hour: '2-digit',
                minute: '2-digit'
            }) : 'เวลาไม่ระบุ';

            // Use correct amount field and handle NaN
            const amount = parseFloat(voucherData.amount || voucherData.amount_paid || 0) || 0;
            const tdsAmount = parseFloat(voucherData.tds_amount || 0) || 0;
            const hasTDS = voucherData.is_tds_receivable || tdsAmount > 0;
            const totalAmount = hasTDS ? amount + tdsAmount : amount;
            const amountInText = totalAmount > 0 ? numberToThaiText(totalAmount) : 'ศูนย์บาทถ้วน';
            
            // Process logo - handle blob/base64/buffer
            let logoSrc = '';
            if (companyInfo.logo) {
                try {
                    let logoData;
                    
                    // Check if it's a Buffer-like object (from database BLOB)
                    if (companyInfo.logo.type === 'Buffer' && Array.isArray(companyInfo.logo.data)) {
                        // Convert Buffer array to base64
                        const uint8Array = new Uint8Array(companyInfo.logo.data);
                        let binary = '';
                        for (let i = 0; i < uint8Array.byteLength; i++) {
                            binary += String.fromCharCode(uint8Array[i]);
                        }
                        logoData = btoa(binary);
                        logoSrc = `data:${companyInfo.logo_type || 'image/png'};base64,${logoData}`;
                    } 
                    // Check if it's already a base64 string
                    else if (typeof companyInfo.logo === 'string') {
                        logoData = companyInfo.logo;
                        
                        if (logoData.startsWith('data:image')) {
                            // Already base64 with prefix
                            logoSrc = logoData;
                        } else if (logoData.startsWith('blob:')) {
                            // Blob URL
                            logoSrc = logoData;
                        } else {
                            // Assume it's base64 without prefix
                            logoSrc = `data:${companyInfo.logo_type || 'image/png'};base64,${logoData}`;
                        }
                    }
                } catch (error) {
                    console.error('Error processing logo:', error);
                    logoSrc = ''; // Fallback to no logo
                }
            }
            
            // Get payment mode with fallback
            const paymentMode = voucherData.payment_mode || voucherData.mode || 'Cash';
            
            // Get reference with fallback
            const referenceNumber = voucherData.reference_id || voucherData.voucher_number || voucherData.reference_number;

            // Helper function within scope
            const getPaymentModeLabel = (mode) => {
                const modes = {
                    'Cash': '💵 เงินสด / Cash',
                    'Bank': '🏦 โอนเงินผ่านธนาคาร / Bank Transfer',
                    'Credit_Card': '💳 บัตรเครดิต / Credit Card',
                    'UPI': '📱 UPI / ยูพีไอ',
                    'Cheque': '📝 เช็ค / Cheque',
                    'Online': '💻 ชำระออนไลน์ / Online Payment'
                };
                return modes[mode] || `💰 ${mode} / ${mode}`;
            };

            const printContent = `
                <!DOCTYPE html>
                <html>
                <head>
                    <meta charset="UTF-8">
                    <title>ใบเสร็จรับเงิน / Receipt</title>
                    <style>
                        @page {
                            size: A4;
                            margin: 10mm 15mm;
                        }
                        
                        * {
                            margin: 0;
                            padding: 0;
                            box-sizing: border-box;
                        }
                        
                        body {
                            font-family: 'Sarabun', 'TH Sarabun New', 'Arial Unicode MS', Arial, sans-serif;
                            font-size: 9px;
                            line-height: 1.2;
                            color: #333;
                            background: white;
                            margin: 0;
                            padding: 0;
                        }
                        
                        .receipt-container {
                            max-width: 190mm;
                            margin: 0 auto;
                            padding: 5mm;
                            background: white;
                        }
                        
                        .receipt-header {
                            border: 2px solid #e67e22;
                            border-radius: 4px;
                            padding: 6px;
                            margin-bottom: 8px;
                            text-align: center;
                            background: linear-gradient(135deg, #fef9e7 0%, #fed7aa 100%);
                        }
                        
                        .company-name {
                            font-size: 14px;
                            font-weight: bold;
                            margin-bottom: 3px;
                            color: #d35400;
                            text-transform: uppercase;
                            letter-spacing: 0.5px;
                        }
                        
                        .company-details {
                            font-size: 8px;
                            line-height: 1.3;
                            color: #495057;
                        }
                        
                        .receipt-title {
                            text-align: center;
                            margin: 6px 0;
                            padding: 6px;
                            border-radius: 4px;
                            background: linear-gradient(135deg, #e67e22 0%, #d35400 100%);
                            color: white;
                        }
                        
                        .title-main {
                            font-size: 13px;
                            font-weight: bold;
                            margin-bottom: 2px;
                        }
                        
                        .title-sub {
                            font-size: 9px;
                            opacity: 0.9;
                        }
                        
                        .section-title {
                            font-size: 10px;
                            font-weight: bold;
                            color: #d35400;
                            margin-bottom: 4px;
                            padding-bottom: 2px;
                            border-bottom: 1px solid #e9ecef;
                        }
                        
                        .receipt-info, .customer-section, .payment-details {
                            margin-bottom: 6px;
                            padding: 5px;
                            background: #fef9e7;
                            border-radius: 3px;
                            border-left: 2px solid #e67e22;
                        }
                        
                        .info-row {
                            display: flex;
                            justify-content: space-between;
                            align-items: center;
                            margin-bottom: 5px;
                            padding: 4px 0;
                            border-bottom: 1px solid #dee2e6;
                        }
                        
                        .info-label {
                            font-weight: 600;
                            color: #495057;
                            min-width: 120px;
                        }
                        
                        .info-value {
                            flex: 1;
                            text-align: right;
                            color: #212529;
                        }
                        
                        .receipt-number {
                            font-weight: bold;
                            color: #d35400;
                            font-family: 'Courier New', monospace;
                        }
                        
                        .customer-name {
                            font-weight: bold;
                            color: #dc3545;
                        }
                        
                        .payment-method {
                            font-weight: bold;
                            color: #28a745;
                        }
                        
                        .reference {
                            font-family: 'Courier New', monospace;
                            background: #e9ecef;
                            padding: 2px 4px;
                            border-radius: 3px;
                        }
                        
                        .amount-section {
                            border: 2px solid #28a745;
                            border-radius: 4px;
                            padding: 6px;
                            margin: 6px 0;
                            background: linear-gradient(135deg, #f8fff8 0%, #e8f5e8 100%);
                        }
                        
                        .amount-header h3 {
                            color: #28a745;
                            margin-bottom: 4px;
                            font-size: 10px;
                        }
                        
                        .amount-row {
                            display: flex;
                            justify-content: space-between;
                            align-items: center;
                            font-size: 11px;
                            margin-bottom: 4px;
                        }
                        
                        .amount-label {
                            font-weight: bold;
                            color: #495057;
                        }
                        
                        .amount-value {
                            font-weight: bold;
                            font-size: 14px;
                            color: #28a745;
                        }
                        
                        .amount-text {
                            margin-top: 4px;
                            padding: 4px;
                            border-radius: 3px;
                            background: rgba(40, 167, 69, 0.1);
                            font-style: italic;
                            color: #495057;
                            text-align: center;
                            border: 1px dashed #28a745;
                            font-size: 8px;
                        }
                        
                        .payment-details {
                            margin: 6px 0;
                        }
                        
                        .signature-section {
                            display: flex;
                            justify-content: space-between;
                            margin-top: 15px;
                            padding: 8px 0;
                        }
                        
                        .signature-box {
                            text-align: center;
                            width: 45%;
                            padding: 5px;
                        }
                        
                        .signature-line {
                            border-top: 2px solid #e67e22;
                            margin-top: 40px;
                            padding-top: 8px;
                            font-weight: bold;
                            color: #495057;
                            background: linear-gradient(135deg, #fef9e7 0%, #fed7aa 100%);
                            border-radius: 3px;
                            padding: 8px;
                            font-size: 9px;
                        }
                        
                        /* Column Layout Styles */
                        .receipt-info-grid {
                            display: grid !important;
                            grid-template-columns: 1fr 1fr !important;
                            gap: 8px !important;
                            margin-bottom: 8px !important;
                            width: 100% !important;
                        }
                        
                        .info-column {
                            background: #fef9e7 !important;
                            padding: 5px !important;
                            border-radius: 3px !important;
                            border-left: 2px solid #e67e22 !important;
                            width: 100% !important;
                            box-sizing: border-box !important;
                            display: block !important;
                        }
                        
                        .column-title {
                            font-size: 9px !important;
                            font-weight: bold !important;
                            color: #d35400 !important;
                            margin-bottom: 3px !important;
                            text-align: center !important;
                            border-bottom: 1px solid #fed7aa !important;
                            padding-bottom: 2px !important;
                        }
                        
                        .compact-info-row {
                            display: flex !important;
                            justify-content: space-between !important;
                            margin-bottom: 2px !important;
                            font-size: 8px !important;
                        }
                        
                        .compact-label {
                            font-weight: 600 !important;
                            color: #d35400 !important;
                            min-width: 50px !important;
                        }
                        
                        .compact-value {
                            color: #212529 !important;
                            text-align: right !important;
                            flex: 1 !important;
                        }
                        
                        .footer {
                            margin-top: 8px;
                            text-align: center;
                            font-size: 7px;
                            color: #6c757d;
                            border-top: 1px solid #dee2e6;
                            padding-top: 5px;
                            background: #f8f9fa;
                            border-radius: 3px;
                            padding: 5px;
                        }
                        
                        .footer p {
                            margin-bottom: 2px;
                        }
                        
                        .tax-notice {
                            background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
                            border: 1px solid #ffc107;
                            border-radius: 3px;
                            padding: 5px;
                            margin: 5px 0;
                            font-size: 7px;
                            text-align: center;
                        }
                        
                        .tax-notice strong {
                            color: #856404;
                        }
                        
                        @media print {
                            body {
                                margin: 0;
                                padding: 0;
                                font-size: 10px;
                            }
                            
                            .receipt-container {
                                max-width: 100%;
                                padding: 5mm;
                                box-shadow: none;
                                min-height: auto;
                            }
                            
                            .receipt-header {
                                background: white !important;
                                border: 1px solid #000 !important;
                                padding: 8px !important;
                            }
                            
                            .receipt-title {
                                background: white !important;
                                color: black !important;
                                border: 1px solid #000;
                                padding: 6px !important;
                            }
                            
                            .amount-section {
                                background: white !important;
                                border: 1px solid #000 !important;
                                padding: 8px !important;
                            }
                            
                            .section-title {
                                color: black !important;
                            }
                            
                            .info-column {
                                background: white !important;
                                border-left: 2px solid #000 !important;
                            }
                            
                            .signature-line {
                                margin-top: 25px !important;
                                border-top: 1px solid #000 !important;
                            }
                            
                            @page {
                                margin: 10mm;
                                size: A4;
                            }
                        }
                    </style>
                </head>
                <body>
                    <div class="receipt-container">
                        <!-- Header with Company Info -->
                        <div class="receipt-header">
                            ${logoSrc ? `
                            <div style="text-align: center; margin-bottom: 5px;">
                                <img src="${logoSrc}" alt="${companyInfo.name || 'Company Logo'}" 
                                     style="max-height: 50px; max-width: 180px; object-fit: contain;" />
                            </div>
                            ` : ''}
                            <div class="company-name">${companyInfo.name || 'ชื่อบริษัท / Company Name'}</div>
                            <div class="company-details">
                                ${companyInfo.address || 'ที่อยู่บริษัท / Company Address'}<br>
                                ${companyInfo.city ? `${companyInfo.city}` : ''} ${companyInfo.state ? `${companyInfo.state}` : ''} ${companyInfo.zip_code ? `${companyInfo.zip_code}` : ''}<br>
                                เลขประจำตัวผู้เสียภาษี / Tax ID: ${companyInfo.tax_id || 'XXXXXXXXXXXXX'}<br>
                                โทรศัพท์ / Tel: ${companyInfo.phone_number || 'XXX-XXX-XXXX'}
                                ${companyInfo.email ? ` | อีเมล: ${companyInfo.email}` : ''}
                            </div>
                        </div>
                        
                        <!-- Receipt Title -->
                        <div class="receipt-title">
                            <div class="title-main">ใบเสร็จรับเงิน</div>
                            <div class="title-sub">PAYMENT RECEIPT</div>
                        </div>
                        
                        <!-- Receipt Information Grid -->
                        <div class="receipt-info-grid">
                            <!-- Column 1: Customer Information -->
                            <div class="info-column">
                                <div class="column-title">ข้อมูลลูกค้า / Customer Info</div>
                                <div class="compact-info-row">
                                    <span class="compact-label">ชื่อ:</span>
                                    <span class="compact-value customer-name">${customerName}</span>
                                </div>
                                <div class="compact-info-row">
                                    <span class="compact-label">ที่อยู่:</span>
                                    <span class="compact-value">${customerAddress}</span>
                                </div>
                                <div class="compact-info-row">
                                    <span class="compact-label">โทร:</span>
                                    <span class="compact-value">${customerPhone}</span>
                                </div>
                            </div>
                            
                            <!-- Column 2: Receipt Details -->
                            <div class="info-column">
                                <div class="column-title">ข้อมูลใบเสร็จ / Receipt Info</div>
                                <div class="compact-info-row">
                                    <span class="compact-label">เลขที่:</span>
                                    <span class="compact-value receipt-number">${voucherData.transaction_id || 'N/A'}</span>
                                </div>
                                <div class="compact-info-row">
                                    <span class="compact-label">วันที่:</span>
                                    <span class="compact-value">${formattedDate}</span>
                                </div>
                                <div class="compact-info-row">
                                    <span class="compact-label">เวลา:</span>
                                    <span class="compact-value">${formattedTime}</span>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Amount Section -->
                        <div class="amount-section">
                            <div class="amount-header">
                                <h3>รายละเอียดการชำระเงิน / Payment Details</h3>
                            </div>
                            ${hasTDS ? `
                            <!-- TDS Bifurcation -->
                            <div class="amount-row">
                                <span class="amount-label">จำนวนเงินที่ได้รับ / Amount Received:</span>
                                <span class="amount-value">฿ ${amount.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                            <div class="amount-row">
                                <span class="amount-label" style="color: #f39c12;">หัก TDS / TDS Deducted:</span>
                                <span class="amount-value" style="color: #f39c12;">฿ ${tdsAmount.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                            <div style="border-top: 2px solid #28a745; margin: 8px 0; padding-top: 8px;">
                                <div class="amount-row">
                                    <span class="amount-label" style="font-size: 16px;">จำนวนเงินรวม / Total Amount:</span>
                                    <span class="amount-value" style="font-size: 20px;">฿ ${totalAmount.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                </div>
                            </div>
                            ` : `
                            <div class="amount-row">
                                <span class="amount-label">จำนวนเงิน / Amount:</span>
                                <span class="amount-value">฿ ${amount.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                            `}
                            <div class="amount-text">
                                <strong>(${amountInText})</strong>
                            </div>
                        </div>
                        
                        <!-- Payment Details -->
                        <div class="info-column" style="margin-bottom: 15px;">
                            <div class="column-title">วิธีการชำระเงิน / Payment Method</div>
                            <div class="compact-info-row">
                                <span class="compact-label">ชำระโดย:</span>
                                <span class="compact-value payment-method">${getPaymentModeLabel(paymentMode)}</span>
                            </div>
                            ${referenceNumber ? `
                            <div class="compact-info-row">
                                <span class="compact-label">เลขอ้างอิง:</span>
                                <span class="compact-value reference">${referenceNumber}</span>
                            </div>
                            ` : ''}
                            ${voucherData.remarks ? `
                            <div class="compact-info-row">
                                <span class="compact-label">หมายเหตุ:</span>
                                <span class="compact-value">${voucherData.remarks}</span>
                            </div>
                            ` : ''}
                        </div>
                        
                        <!-- Tax Notice -->
                        <div class="tax-notice">
                            <strong>🏛️ ใบเสร็จรับเงินฉบับนี้ออกตามกฎหมายภาษีอากรของประเทศไทย</strong><br>
                            <strong>🏛️ This receipt is issued in accordance with Thai tax laws</strong>
                        </div>
                        
                        <!-- Signature Section -->
                        <div class="signature-section">
                            <div class="signature-box">
                                <div class="signature-line">
                                    ผู้รับเงิน / Received by
                                </div>
                            </div>
                            <div class="signature-box">
                                <div class="signature-line">
                                    ผู้จ่ายเงิน / Paid by
                                </div>
                            </div>
                        </div>
                        
                        ${companyInfo.bank_name || companyInfo.account_number ? `
                        <!-- Bank Information -->
                        <div class="bank-info" style="background: #f8f9fa; padding: 4px; border-radius: 3px; margin: 4px 0; font-size: 7px; text-align: center; border: 1px solid #dee2e6;">
                            <strong style="color: #2c5282;">🏛️ ข้อมูลบัญชีธนาคาร / Bank Details</strong><br>
                            ${companyInfo.bank_name ? `ธนาคาร: ${companyInfo.bank_name}` : ''}
                            ${companyInfo.account_name ? ` | ชื่อบัญชี: ${companyInfo.account_name}` : ''}
                            ${companyInfo.account_number ? ` | เลขบัญชี: ${companyInfo.account_number}` : ''}
                            ${companyInfo.swift_code ? ` | SWIFT: ${companyInfo.swift_code}` : ''}
                        </div>
                        ` : ''}
                        
                        ${companyInfo.terms_and_conditions ? `
                        <!-- Terms and Conditions -->
                        <div style="background: #fef9e7; padding: 4px; border-radius: 3px; margin: 4px 0; font-size: 6px; color: #495057; border-left: 2px solid #e67e22;">
                            <strong>เงื่อนไข / Terms:</strong> ${companyInfo.terms_and_conditions}
                        </div>
                        ` : ''}
                        
                        <!-- Footer -->
                        <div class="footer">
                            <p><strong>📄 เอกสารออกโดยระบบอัตโนมัติ / Automatically generated</strong></p>
                            <p>📞 ติดต่อสอบถาม / Inquiries: ${companyInfo.phone_number || 'XXX-XXX-XXXX'} | 💻 StoreHub POS</p>
                            <p style="font-size: 8px; color: #adb5bd; margin-top: 3px;">ขอขอบคุณที่ใช้บริการ / Thank you for your business</p>
                        </div>
                    </div>
                </body>
                </html>
            `;

            // Open print window with better handling
            const printWindow = window.open("", "_blank", "width=800,height=600,scrollbars=yes");
            
            if (!printWindow) {
                toast.error("Pop-up blocked! Please allow pop-ups for this site.");
                setLoading(false);
                return;
            }
            
            printWindow.document.write(printContent);
            printWindow.document.close();

            printWindow.onload = () => {
                printWindow.focus();
                setTimeout(() => {
                    printWindow.print();
                    // Optional: Close window after printing
                    printWindow.onafterprint = () => {
                        printWindow.close();
                    };
                }, 500);
            };

            // Handle print window errors
            printWindow.onerror = (error) => {
                console.error("Print window error:", error);
                toast.error("Error opening print window");
            };

            setLoading(false);
            toast.success("Receipt generated successfully!");
        } catch (error) {
            console.error("Error printing receipt:", error);
            toast.error("Error generating receipt");
            setLoading(false);
        }
    };

    // Helper function to convert number to Thai text
    const numberToThaiText = (number) => {
        const digits = ['ศูนย์', 'หนึ่ง', 'สอง', 'สาม', 'สี่', 'ห้า', 'หก', 'เจ็ด', 'แปด', 'เก้า'];
        const positions = ['', 'สิบ', 'ร้อย', 'พัน', 'หมื่น', 'แสน', 'ล้าน'];
        
        if (number === 0) return 'ศูนย์บาทถ้วน';
        
        const [intPart, decPart] = number.toFixed(2).split('.');
        let result = '';
        
        // Convert integer part
        const intStr = intPart.toString();
        const len = intStr.length;
        
        for (let i = 0; i < len; i++) {
            const digit = parseInt(intStr[i]);
            const position = len - i - 1;
            
            if (digit === 0) continue;
            
            if (position === 1 && digit === 1) {
                result += 'สิบ';
            } else if (position === 1 && digit === 2) {
                result += 'ยี่สิบ';
            } else if (position === 0 && digit === 1 && len > 1) {
                result += 'เอ็ด';
            } else {
                result += digits[digit] + positions[position];
            }
        }
        
        result += 'บาท';
        
        // Convert decimal part
        if (parseInt(decPart) === 0) {
            result += 'ถ้วน';
        } else {
            const satangStr = decPart.toString();
            let satangText = '';
            
            for (let i = 0; i < satangStr.length; i++) {
                const digit = parseInt(satangStr[i]);
                const position = satangStr.length - i - 1;
                
                if (digit === 0 && position === 1) continue;
                
                if (position === 1 && digit === 1) {
                    satangText += 'สิบ';
                } else if (position === 1 && digit === 2) {
                    satangText += 'ยี่สิบ';
                } else if (position === 0 && digit === 1 && satangStr.length > 1) {
                    satangText += 'เอ็ด';
                } else {
                    satangText += digits[digit];
                    if (position === 1) satangText += 'สิบ';
                }
            }
            
            result += satangText + 'สตางค์';
        }
        
        return result;
    };
    useEffect(() => {
        const fetchAndSetData = async () => {
            try {
                const result = await fetchData("receipt_vouchers", setData, "id", {});
                console.log("Fetched receipt vouchers data:", result);
                if (result && result.length > 0) {
                    console.log("Sample record structure:", result[0]);
                    console.log("Available fields:", Object.keys(result[0]));
                }
            } catch (error) {
                console.error("Error in useEffect:", error);
            }
        };

        fetchAndSetData();
    }, []);

    // Filter data based on search and filter mode
    const filteredData = data.filter(item => {
        const matchesSearch = 
            item.transaction_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.customer_id?.toString().includes(searchTerm) ||
            (item.amount || item.amount_paid || 0)?.toString().includes(searchTerm);
        
        const matchesFilter = filterMode === "All" || (item.payment_mode || item.mode) === filterMode;
        
        return matchesSearch && matchesFilter;
    });

    // Pagination calculations
    const totalRecords = filteredData.length;
    const totalPages = Math.ceil(totalRecords / recordsPerPage);
    const indexOfLastRecord = currentPage * recordsPerPage;
    const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
    const currentRecords = filteredData.slice(indexOfFirstRecord, indexOfLastRecord);

    // Reset to page 1 when filter changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, filterMode, recordsPerPage]);

    // Generate page numbers to display
    const getPageNumbers = () => {
        const pages = [];
        const maxPagesToShow = 5;
        
        if (totalPages <= maxPagesToShow) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            if (currentPage <= 3) {
                for (let i = 1; i <= maxPagesToShow; i++) {
                    pages.push(i);
                }
            } else if (currentPage >= totalPages - 2) {
                for (let i = totalPages - maxPagesToShow + 1; i <= totalPages; i++) {
                    pages.push(i);
                }
            } else {
                for (let i = currentPage - 2; i <= currentPage + 2; i++) {
                    pages.push(i);
                }
            }
        }
        return pages;
    };

    return (
        <Layout>
            <Header title="Receipt Vouchers / ใบเสร็จรับเงิน" />
            <ToastContainer />

            {loading && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 9999
                }}>
                    <div style={{ 
                        backgroundColor: 'white', 
                        padding: '30px', 
                        borderRadius: '10px',
                        textAlign: 'center'
                    }}>
                        <div className="spinner-border text-primary mb-3" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <div>Processing...</div>
                    </div>
                </div>
            )}

            <div className="row">
                {/* Left Panel - Payment Form */}
                <div className="col-lg-4 col-sm-12 mb-4">
                    <CardComponent title="Create Receipt Voucher" headerColor="primary">
                        <form onSubmit={handleSubmit} className="voucher-form">
                            {/* Customer Selection */}
                            <div className="form-group mb-3">
                                <label className="form-label d-flex align-items-center">
                                    <FaUser className="me-2" />
                                    Customer Name <span className="text-danger">*</span>
                                </label>
                                <select 
                                    value={selectedCustomer} 
                                    onChange={getOutStandingBalance} 
                                    className="form-control form-select"
                                    required
                                >
                                    <option value="">-- Select Customer --</option>
                                    {customers.map((customer) => (
                                        <option key={customer.id} value={customer.id}>
                                            {customer.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Outstanding Amount */}
                            <div className="form-group mb-3">
                                <label className="form-label d-flex align-items-center">
                                    <FaMoneyBillWave className="me-2" />
                                    Outstanding Amount
                                </label>
                                <input 
                                    type="text" 
                                    value={`฿ ${outstandingAmount.toLocaleString('th-TH', { minimumFractionDigits: 2 })}`} 
                                    readOnly 
                                    className="form-control bg-light"
                                />
                            </div>

                            {/* Payment Mode */}
                            <div className="form-group mb-3">
                                <label className="form-label d-flex align-items-center">
                                    <FaCreditCard className="me-2" />
                                    Payment Mode <span className="text-danger">*</span>
                                </label>
                                <select 
                                    value={paymentMode} 
                                    onChange={(e) => {
                                        setPaymentMode(e.target.value);
                                        setBankAccountId(''); // Reset bank account when mode changes
                                    }} 
                                    className="form-control form-select"
                                    required
                                >
                                    <option value="">-- Select Payment Mode --</option>
                                    {paymentModes.map(mode => (
                                        <option key={mode.value} value={mode.value}>
                                            {mode.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Bank Account Selection - Only show when bank is required */}
                            {paymentMode && paymentModes.find(mode => mode.value === paymentMode)?.requiresBank && (
                                <div className="form-group mb-3">
                                    <label className="form-label d-flex align-items-center">
                                        <FaCreditCard className="me-2" />
                                        Bank Account <span className="text-danger">*</span>
                                    </label>
                                    <select
                                        value={bankAccountId}
                                        onChange={(e) => setBankAccountId(e.target.value)}
                                        className="form-control form-select"
                                        required
                                    >
                                        <option value="">Select Bank Account</option>
                                        {bankAccounts.filter(acc => acc.is_active).map(account => (
                                            <option key={account.id} value={account.id}>
                                                {account.account_name} ({account.bank_name}) - ฿{account.current_balance?.toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {/* TDS Receivable Checkbox */}
                            <div className="form-group mb-3">
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="tdsCheckbox"
                                        checked={isTdsReceivable}
                                        onChange={(e) => {
                                            setIsTdsReceivable(e.target.checked);
                                            if (!e.target.checked) {
                                                setTdsAmount('');
                                            }
                                        }}
                                    />
                                    <label className="form-check-label" htmlFor="tdsCheckbox">
                                        📊 Customer Deducted TDS (Tax Deducted at Source)
                                    </label>
                                </div>
                                <small className="text-muted">Check this if customer deducted TDS from payment</small>
                            </div>

                            {/* TDS Amount - Only show when checkbox is checked */}
                            {isTdsReceivable && (
                                <div className="form-group mb-3">
                                    <label className="form-label d-flex align-items-center">
                                        <FaMoneyBillWave className="me-2" />
                                        TDS Amount (Deducted by Customer) <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={tdsAmount}
                                        onChange={(e) => setTdsAmount(e.target.value)}
                                        className="form-control"
                                        placeholder="0.00"
                                        required
                                    />
                                    <small className="text-muted">This amount will be added to outstanding balance</small>
                                </div>
                            )}

                            {/* Amount Paid */}
                            <div className="form-group mb-3">
                                <label className="form-label d-flex align-items-center">
                                    <FaMoneyBillWave className="me-2" />
                                    {isTdsReceivable ? 'Amount Received (After TDS Deduction)' : 'Amount Paid'} <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={paymentAmount}
                                    onChange={(e) => setPaymentAmount(e.target.value)}
                                    className="form-control"
                                    placeholder="0.00"
                                    required
                                />
                                {isTdsReceivable && (
                                    <small className="text-info d-block mt-1">
                                        💡 Enter the actual amount received in your bank account (after TDS deduction)
                                    </small>
                                )}
                            </div>

                            {/* TDS Calculation Summary */}
                            {isTdsReceivable && tdsAmount && paymentAmount && (
                                <div className="mb-3" style={{
                                    background: 'linear-gradient(135deg, #f5f7fa 0%, #e8f4f8 100%)',
                                    border: '1px solid #d4e7f0',
                                    borderRadius: '8px',
                                    padding: '12px 15px'
                                }}>
                                    <strong style={{ color: '#2c5282' }}>📊 TDS Summary:</strong><br/>
                                    <small style={{ color: '#4a5568' }}>
                                        • Amount Received: ฿{parseFloat(paymentAmount).toLocaleString('th-TH', { minimumFractionDigits: 2 })}<br/>
                                        • TDS Deducted: ฿{parseFloat(tdsAmount).toLocaleString('th-TH', { minimumFractionDigits: 2 })}<br/>
                                        • Total Invoice Amount: ฿{(parseFloat(paymentAmount) + parseFloat(tdsAmount)).toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                                    </small>
                                </div>
                            )}

                            {/* Reference Number */}
                            <div className="form-group mb-3">
                                <label className="form-label">
                                    Reference Number (Optional)
                                </label>
                                <input
                                    type="text"
                                    value={referenceNumber}
                                    onChange={(e) => setReferenceNumber(e.target.value)}
                                    className="form-control"
                                    placeholder="Enter reference number"
                                />
                            </div>

                            {/* Remarks */}
                            <div className="form-group mb-3">
                                <label className="form-label">Remarks (Optional)</label>
                                <textarea 
                                    value={remarks} 
                                    onChange={(e) => setRemarks(e.target.value)} 
                                    className="form-control"
                                    rows="3"
                                    placeholder="Enter any additional notes"
                                />
                            </div>

                            {/* Balance After Payment */}
                            <div className="form-group mb-4">
                                <label className="form-label d-flex align-items-center">
                                    <FaMoneyBillWave className="me-2" />
                                    Balance After Payment
                                </label>
                                <input 
                                    type="text" 
                                    value={`฿ ${balanceAfterPayment.toLocaleString('th-TH', { minimumFractionDigits: 2 })}`} 
                                    readOnly 
                                    className={`form-control ${balanceAfterPayment > 0 ? 'bg-warning' : 'bg-success text-white'}`}
                                    style={{ fontWeight: 'bold' }}
                                />
                            </div>

                            {/* Submit Button */}
                            <button 
                                type="submit" 
                                className="btn btn-primary btn-lg w-100"
                                disabled={loading}
                            >
                                <FaReceipt className="me-2" />
                                {loading ? 'Processing...' : 'Save Payment'}
                            </button>
                        </form>
                    </CardComponent>
                </div>

                {/* Right Panel - Payment History Table */}
                <div className="col-lg-8 col-md-8 col-sm-12 mb-4">
                    <CardComponent title="Payment History" headerColor="success">
                        {/* Search and Filter Section */}
                        <div className="row mb-3">
                            <div className="col-md-3 mb-2">
                                <select
                                    className="form-control form-select"
                                    value={recordsPerPage}
                                    onChange={(e) => setRecordsPerPage(Number(e.target.value))}
                                >
                                    <option value={5}>5 records</option>
                                    <option value={10}>10 records</option>
                                    <option value={25}>25 records</option>
                                    <option value={50}>50 records</option>
                                    <option value={100}>100 records</option>
                                </select>
                            </div>
                            <div className="col-md-5 mb-2">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="🔍 Search by Transaction ID, Customer ID, Amount..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                            <div className="col-md-4 mb-2">
                                <select
                                    className="form-control form-select"
                                    value={filterMode}
                                    onChange={(e) => setFilterMode(e.target.value)}
                                >
                                    <option value="All">All Payment Modes</option>
                                    <option value="Cash">Cash</option>
                                    <option value="Bank">Bank</option>
                                    <option value="Cheque">Cheque</option>
                                    <option value="Online">Online</option>
                                </select>
                            </div>
                        </div>

                        {/* Table */}
                        {filteredData.length === 0 ? (
                            <div className="alert alert-info text-center">
                                <FaReceipt size={50} className="mb-3" />
                                <p>No payment records found</p>
                            </div>
                        ) : (
                            <div className="table-responsive" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                                <table className="table table-hover table-bordered">
                                    <thead className="table-dark sticky-top">
                                        <tr>
                                            <th>Txn ID</th>
                                            <th>Customer</th>
                                            <th>Date</th>
                                            <th>Mode</th>
                                            <th>Amount</th>
                                            <th>TDS Amount</th>
                                            <th>Ref ID</th>
                                            <th className="text-center">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentRecords.map((item) => {
                                            const customer = customers.find(c => c.id === parseInt(item.customer_id || item.customer_name));
                                            const customerName = customer ? customer.name : (item.customer_name || `Customer #${item.customer_id || 'Unknown'}`);
                                            
                                            return (
                                                <tr key={item.id}>
                                                    <td className="fw-bold">{item.transaction_id}</td>
                                                    <td>{customerName}</td>
                                                    <td>{item.receipt_date || item.payment_date ? new Date(item.receipt_date || item.payment_date).toLocaleString('th-TH') : 'Invalid Date'}</td>
                                                    <td>
                                                        <span className={`badge ${
                                                            (item.payment_mode || item.mode) === 'Cash' ? 'bg-success' :
                                                            (item.payment_mode || item.mode) === 'Bank' ? 'bg-primary' :
                                                            (item.payment_mode || item.mode) === 'Cheque' ? 'bg-warning' :
                                                            'bg-info'
                                                        }`}>
                                                            {item.payment_mode || item.mode || 'Unknown'}
                                                        </span>
                                                    </td>
                                                    <td className="fw-bold">฿ {(parseFloat(item.amount || item.amount_paid || 0) || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</td>
                                                    <td className="fw-bold text-warning">
                                                        {item.tds_amount && parseFloat(item.tds_amount) > 0 ? (
                                                            <>
                                                                📊 ฿{parseFloat(item.tds_amount).toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                                                            </>
                                                        ) : '-'}
                                                    </td>
                                                    <td>{item.reference_id || item.voucher_number || '-'}</td>
                                                    <td className="text-center">
                                                        <button
                                                            className="btn btn-sm btn-primary"
                                                            onClick={() => handlePrintReceipt(item)}
                                                            title="Print Receipt"
                                                        >
                                                            <FaPrint /> Print
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Pagination Controls */}
                        {filteredData.length > 0 && (
                            <div className="d-flex justify-content-between align-items-center mt-3 mb-3" style={{
                                background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                                padding: '10px 15px',
                                borderRadius: '8px',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}>
                                <div className="text-muted" style={{ fontSize: '13px', fontWeight: '500' }}>
                                    Showing {indexOfFirstRecord + 1} to {Math.min(indexOfLastRecord, totalRecords)} of {totalRecords} records
                                </div>
                                <div>
                                    <nav>
                                        <ul className="pagination mb-0" style={{ gap: '4px' }}>
                                            {/* First Button */}
                                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                <button 
                                                    className="page-link"
                                                    onClick={() => setCurrentPage(1)}
                                                    disabled={currentPage === 1}
                                                    style={{
                                                        padding: '4px 10px',
                                                        fontSize: '12px',
                                                        borderRadius: '5px',
                                                        border: '1px solid #dee2e6',
                                                        background: currentPage === 1 ? '#e9ecef' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                        color: currentPage === 1 ? '#6c757d' : 'white',
                                                        fontWeight: '500',
                                                        transition: 'all 0.3s ease'
                                                    }}
                                                >
                                                    First
                                                </button>
                                            </li>
                                            
                                            {/* Previous Button */}
                                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                <button 
                                                    className="page-link"
                                                    onClick={() => setCurrentPage(currentPage - 1)}
                                                    disabled={currentPage === 1}
                                                    style={{
                                                        padding: '4px 10px',
                                                        fontSize: '12px',
                                                        borderRadius: '5px',
                                                        border: '1px solid #dee2e6',
                                                        background: currentPage === 1 ? '#e9ecef' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                        color: currentPage === 1 ? '#6c757d' : 'white',
                                                        fontWeight: '500',
                                                        transition: 'all 0.3s ease'
                                                    }}
                                                >
                                                    Prev
                                                </button>
                                            </li>
                                            
                                            {/* Page Numbers */}
                                            {getPageNumbers().map(pageNum => (
                                                <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                                                    <button 
                                                        className="page-link"
                                                        onClick={() => setCurrentPage(pageNum)}
                                                        style={{
                                                            padding: '4px 10px',
                                                            fontSize: '12px',
                                                            borderRadius: '5px',
                                                            border: '1px solid #dee2e6',
                                                            background: currentPage === pageNum 
                                                                ? 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' 
                                                                : 'white',
                                                            color: currentPage === pageNum ? 'white' : '#495057',
                                                            fontWeight: currentPage === pageNum ? '600' : '500',
                                                            minWidth: '32px',
                                                            transition: 'all 0.3s ease',
                                                            boxShadow: currentPage === pageNum ? '0 2px 8px rgba(240, 147, 251, 0.4)' : 'none'
                                                        }}
                                                    >
                                                        {pageNum}
                                                    </button>
                                                </li>
                                            ))}
                                            
                                            {/* Next Button */}
                                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                <button 
                                                    className="page-link"
                                                    onClick={() => setCurrentPage(currentPage + 1)}
                                                    disabled={currentPage === totalPages}
                                                    style={{
                                                        padding: '4px 10px',
                                                        fontSize: '12px',
                                                        borderRadius: '5px',
                                                        border: '1px solid #dee2e6',
                                                        background: currentPage === totalPages ? '#e9ecef' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                        color: currentPage === totalPages ? '#6c757d' : 'white',
                                                        fontWeight: '500',
                                                        transition: 'all 0.3s ease'
                                                    }}
                                                >
                                                    Next
                                                </button>
                                            </li>
                                            
                                            {/* Last Button */}
                                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                <button 
                                                    className="page-link"
                                                    onClick={() => setCurrentPage(totalPages)}
                                                    disabled={currentPage === totalPages}
                                                    style={{
                                                        padding: '4px 10px',
                                                        fontSize: '12px',
                                                        borderRadius: '5px',
                                                        border: '1px solid #dee2e6',
                                                        background: currentPage === totalPages ? '#e9ecef' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                        color: currentPage === totalPages ? '#6c757d' : 'white',
                                                        fontWeight: '500',
                                                        transition: 'all 0.3s ease'
                                                    }}
                                                >
                                                    Last
                                                </button>
                                            </li>
                                        </ul>
                                    </nav>
                                </div>
                            </div>
                        )}

                        {/* Summary */}
                        <div className="mt-3 p-3 bg-light border rounded">
                            <div className="row">
                                <div className="col-md-4 text-center">
                                    <h6>Total Records</h6>
                                    <h4 className="text-primary">{filteredData.length}</h4>
                                </div>
                                <div className="col-md-4 text-center">
                                    <h6>Total Amount</h6>
                                    <h4 className="text-success">
                                        ฿ {filteredData.reduce((sum, item) => sum + (parseFloat(item.amount || item.amount_paid || 0) || 0), 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                                    </h4>
                                </div>
                                <div className="col-md-4 text-center">
                                    <h6>Latest Payment</h6>
                                    <h4 className="text-info">
                                        {filteredData.length > 0 && (filteredData[0].receipt_date || filteredData[0].payment_date) ? new Date(filteredData[0].receipt_date || filteredData[0].payment_date).toLocaleDateString('th-TH') : '-'}
                                    </h4>
                                </div>
                            </div>
                        </div>
                    </CardComponent>
                </div>
            </div>
        </Layout>
    );
}
